/**
 * RevealEntrance — AM3Reveal per design canvas
 * (services/astro-bot-miniapp/design/calestea/project/flow-avatar-mini.jsx §AM3).
 *
 * Triple frame stack (-7° / 0° / +7° rotation, center w/ HD badge),
 * italic Spectral title "N HD frames, brewed for you", body copy,
 * dot indicator with active head. Tap anywhere → onComplete().
 *
 * Frames render as gradient placeholders (design-pending-review) until
 * real preview assets ship under public/assets/sku-previews/.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AvatarFrame } from '../../types/avatarFlow';
import { CosmosBackdrop } from '../brand/CosmosBackdrop';
import styles from './RevealEntrance.module.css';

export interface RevealEntranceProps {
  /** Frames being revealed — first 3 used for hero stack, count drives dots. */
  frames?: AvatarFrame[];
  totalFrames?: number;
  onComplete: () => void;
  /** Auto-advance after N ms. Tap also advances. Default 4000 ms. */
  durationMs?: number;
  testId?: string;
}

const STACK_GRADIENTS = [
  'linear-gradient(150deg, rgba(232,154,60,0.62), rgba(122,61,255,0.45), rgba(13,8,36,0.95))',
  'linear-gradient(150deg, rgba(240,200,80,0.62), rgba(184,107,40,0.40), rgba(13,8,36,0.95))',
  'linear-gradient(150deg, rgba(122,61,255,0.55), rgba(59,91,255,0.40), rgba(13,8,36,0.95))',
];

export function RevealEntrance({
  frames = [],
  totalFrames,
  onComplete,
  durationMs = 4000,
  testId = 'reveal-entrance',
}: RevealEntranceProps): JSX.Element {
  const { t } = useTranslation('flows');
  const completedRef = useRef(false);
  const total = totalFrames ?? frames.length ?? 9;

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, durationMs);
    return () => window.clearTimeout(id);
  }, [onComplete, durationMs]);

  function trigger(): void {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }

  const stackFrames = useMemo(() => {
    const out: { src: string | null; rotateDeg: number; offsetX: number; offsetY: number; z: number; badge?: string }[] = [];
    out.push({ src: frames[1]?.imageUrl ?? null, rotateDeg: -7, offsetX: -22, offsetY: -10, z: 1 });
    out.push({ src: frames[2]?.imageUrl ?? null, rotateDeg: 7, offsetX: 22, offsetY: -8, z: 1 });
    out.push({ src: frames[0]?.imageUrl ?? null, rotateDeg: 0, offsetX: 0, offsetY: 0, z: 2, badge: 'HD' });
    return out;
  }, [frames]);

  return (
    <button
      type="button"
      className={styles.root}
      data-testid={testId}
      onClick={trigger}
      aria-label={t('avatarFlow.reveal.tapToContinue', {
        defaultValue: 'Tap to see all your frames',
      })}
    >
      <CosmosBackdrop />
      <div className={styles.wash} aria-hidden="true" />

      <div className={styles.content}>
        <div className={styles.kicker}>
          {t('avatarFlow.reveal.kicker', { defaultValue: 'Your Pack · ready' })}
        </div>
        <h1 className={styles.title}>
          {t('avatarFlow.reveal.titlePrefix', {
            count: total,
            defaultValue: '{{count}} HD frames,',
          })}{' '}
          <em className={styles.titleAccent}>
            {t('avatarFlow.reveal.titleSuffix', {
              defaultValue: 'brewed for you',
            })}
          </em>
        </h1>

        <div className={styles.stack} aria-hidden="true">
          {stackFrames.map((f, i) => (
            <div
              key={i}
              className={styles.stackTile}
              style={{
                transform: `translate(${f.offsetX}px, ${f.offsetY}px) rotate(${f.rotateDeg}deg)`,
                opacity: i === 2 ? 1 : 0.62,
                zIndex: f.z,
                background: f.src ? undefined : STACK_GRADIENTS[i % 3],
              }}
            >
              {f.src ? (
                <img src={f.src} alt="" className={styles.stackImg} />
              ) : (
                <span className={styles.stackGlyph}>✦</span>
              )}
              {f.badge ? <span className={styles.stackBadge}>{f.badge}</span> : null}
            </div>
          ))}
        </div>

        <p className={styles.subtitle}>
          {t('avatarFlow.reveal.subtitle', {
            defaultValue:
              'No watermark, no Calestea brand. Yours to set as your Telegram, Discord, or anywhere else.',
          })}
        </p>

        <div className={styles.dots} aria-hidden="true">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={i === 0 ? styles.dotHead : styles.dot}
            />
          ))}
        </div>

        <div className={styles.tapHint}>
          {t('avatarFlow.reveal.tapHint', {
            defaultValue: 'Tap to see all frames →',
          })}
        </div>
      </div>
    </button>
  );
}

export default RevealEntrance;
